// src/pages/trading-portal/TradeExecution/AIAgentDataSimulator.js
// Data Simulator for an AI agent: fake market data the user generates, edits, and runs the
// agent over.
//
// A scenario is bars (OHLCV with the VWAP bands, ATR, CMF and BBW the agent reads), the market
// context around them, context changes from a bar on, a starting position and the capital. A run
// replays the bars one close at a time through the agent's own decision layer on the server
// (AIAgentSimulator.py): its rule, the router or its prompt, with simulated fills and no order
// sent. Runs advance in batches; this tab keeps asking while a run is PAUSED.
//
// AIAgentPage renders this tab keyed by the agent, and hands it `renderSnapshot`, its own market
// data grid, so an inspected bar reads exactly like a Decision Log snapshot.
//
// The AI Agent Simulator page renders it too, as `variant="multiDate"`: scenarios that are a range
// of dates (AIAgentMultiDateGenerator), on that page's own routes and list, run back to back and
// reported by date.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Skeleton,
    Alert,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel,
    CircularProgress,
    LinearProgress,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Grid,
    Slider,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
    createChart, ColorType, CandlestickSeries, HistogramSeries, LineSeries, LineStyle, createSeriesMarkers,
} from 'lightweight-charts';
import axiosInstance from '../../../utils/axiosInstance';
import { TimeFrameLabels } from '../stockTradingEnums';
import MultiDateGeneratorFields, { multiDateBody, multiDateForm } from './AIAgentMultiDateGenerator';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const BARS_PER_PAGE = 25;
const STEPS_PER_PAGE = 25;
// A chart of more bars than this opens on its last ones, or on the date chosen, instead of
// squeezing them all in. A single-date scenario never has more.
const FIT_BARS = 500;
// Replay speeds, in bars a second.
const PLAY_SPEEDS = [1, 2, 5, 10];

// The editable bar columns after Time. Optional ones may be blank: the agent then reads the
// field as not available, as it would with no indicator data.
const BAR_COLUMNS = [
    { key: 'open', label: 'Open' },
    { key: 'high', label: 'High' },
    { key: 'low', label: 'Low' },
    { key: 'close', label: 'Close' },
    { key: 'volume', label: 'Volume' },
    { key: 'vwap_upper', label: 'VWAP Up', optional: true },
    { key: 'vwap_mid', label: 'VWAP Mid', optional: true },
    { key: 'vwap_lower', label: 'VWAP Low', optional: true },
    { key: 'atr', label: 'ATR', optional: true },
    { key: 'cmf', label: 'CMF', optional: true },
    { key: 'bbw', label: 'BBW', optional: true },
    { key: 'price', label: 'Live Price', optional: true, hint: 'The current price at the bar close. Blank: the close.' },
];

const TIMEFRAME_SECONDS = {
    ONE_MIN: 60, TWO_MIN: 120, THREE_MIN: 180, FOUR_MIN: 240, FIVE_MIN: 300, FIFTEEN_MIN: 900,
    ONE_HOUR: 3600, TWO_HOUR: 7200, FOUR_HOUR: 14400, FIVE_HOUR: 18000, DAILY: 86400,
};

const FILL_MODEL_LABELS = {
    INSTANT: 'Instant: at the order\'s price, at the bar close',
    NEXT_BAR: 'Next bar: market at the next open, a limit when price trades through it',
};
const OPTIONS_LEVEL_LABELS = {
    AUTO: 'Auto: Put Wall below the low, Call Wall above the high',
    LIVE: 'The agent\'s live options levels',
    NONE: 'None (N/A)',
};

const RUN_STATUS = {
    PAUSED: { label: 'Paused', color: '#b45309', bg: '#fef3c7' },
    COMPLETED: { label: 'Completed', color: '#16a34a', bg: '#dcfce7' },
    FAILED: { label: 'Failed', color: '#dc2626', bg: '#fee2e2' },
};
const OUTCOME = {
    FILLED: { label: 'Filled', color: '#16a34a', bg: '#dcfce7' },
    WORKING: { label: 'Working', color: '#0369a1', bg: '#e0f2fe' },
    SKIPPED_WORKING_ORDER: { label: 'Held: order working', color: '#b45309', bg: '#fef3c7' },
    NOT_SENT: { label: 'Not sent', color: '#dc2626', bg: '#fee2e2' },
    NOT_SIMULATED: { label: 'Not simulated', color: '#64748b', bg: '#f1f5f9' },
    HOLD: { label: 'Hold', color: '#64748b', bg: '#f1f5f9' },
};
const STEP_STATUS = {
    CLOSED: { label: 'Window closed: no cycle', color: '#94a3b8', bg: '#f8fafc' },
    KILL_SWITCH: { label: 'Kill switch', color: '#b91c1c', bg: '#fee2e2' },
    FAILED: { label: 'Failed', color: '#dc2626', bg: '#fee2e2' },
};
const ORDER_STATUS = {
    FILLED: { color: '#16a34a', bg: '#dcfce7' },
    WORKING: { color: '#0369a1', bg: '#e0f2fe' },
    CANCELLED: { color: '#64748b', bg: '#f1f5f9' },
    EXPIRED: { color: '#64748b', bg: '#f1f5f9' },
};
const ACTION_COLORS = {
    BUY: '#16a34a', COVER: '#0ea5e9', SELL: '#dc2626', SHORT: '#f59e0b', CLOSE_ALL: '#dc2626',
    EMERGENCY_EXIT: '#b91c1c', HOLD: '#64748b', STRANGLE_SELL: '#8b5cf6', STRANGLE_CLOSE: '#0ea5e9',
};
// A rule step's result, as the Rule Editor shows "Test on live data".
const TRACE_STATUS = {
    FIRED: { label: 'Fires', bg: '#dcfce7', color: '#16a34a' },
    HELD: { label: 'Matched, not sent', bg: '#fef3c7', color: '#b45309' },
    NOT_MET: { label: 'Not met', bg: '#f1f5f9', color: '#64748b' },
    SKIPPED: { label: 'Not reached', bg: '#f1f5f9', color: '#94a3b8' },
    OFF: { label: 'Off', bg: '#f1f5f9', color: '#94a3b8' },
};
const CONDITION_COLORS = { PASS: '#16a34a', FAIL: '#dc2626', NA: '#d97706' };

const ENGINE_NOTES = {
    ROUTER: 'The router\'s 5-second risk exits (trailing stop, Protocols A to C) and the 15:45 / 15:55 CT '
        + 'end-of-day sell run outside cycles, so they are not simulated.',
    LLM: 'Each bar is one Gemini call, billed like a live cycle. A run takes at most 100 bars.',
};

const DISCARD_CHANGES = 'Discard the changes you have not saved?';

const clone = (value) => JSON.parse(JSON.stringify(value));

// One message per place; the first one the server reported for it wins.
const errorsByPath = (list) => {
    const out = {};
    (list || []).forEach((item) => {
        if (!(item.path in out)) out[item.path] = item.message;
    });
    return out;
};

const apiMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

const fmtPrice = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    return n.toLocaleString(undefined, { minimumFractionDigits: n >= 1 ? 2 : 4, maximumFractionDigits: n >= 1 ? 2 : 6 });
};
const fmtQty = (value) => (value === null || value === undefined ? '—'
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 9 }));
const fmtMoney = (value) => {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pnlColor = (value) => (value > 0 ? '#16a34a' : value < 0 ? '#dc2626' : '#64748b');
// A rule value as the trace shows it: a flag as true / false, text as written, numbers in full.
const traceValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') return fmtQty(value);
    return String(value);
};
// A cell's text, as the input shows it: blank for no value, the number as JSON wrote it.
const cellText = (value) => (value === null || value === undefined ? '' : String(value));
// A step's trading day: the session it ran in (a window across midnight is one day), or for a run
// from before steps carried it, its bar's date.
const sessionOf = (step) => step.session || String(step.time).slice(0, 10);
const weekdayOf = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });

// A cell's text as a value: a finite number, null for a blank optional cell, undefined when
// the text is not a number (the edit is then refused).
const parseCell = (text, optional) => {
    const trimmed = String(text ?? '').trim();
    if (trimmed === '') return optional ? null : undefined;
    const number = Number(trimmed);
    return Number.isFinite(number) ? number : undefined;
};

// Bar labels are wall time in the agent's timezone. Read as UTC, lightweight-charts' UTC axis
// prints the same digits (the Live Market Data chart does the same).
const toChartTime = (label) => {
    if (typeof label !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) return label;
    const ms = Date.parse(`${label.replace(' ', 'T')}:00Z`);
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
};

const pad = (n) => String(n).padStart(2, '0');
// The label one bar after `label`, for an appended bar. It may fall outside the agent's
// trading window; such a bar trades but runs no cycle.
const nextBarLabel = (label, timeframe) => {
    const daily = timeframe === 'DAILY';
    const ms = Date.parse(daily ? `${label}T00:00:00Z` : `${label.replace(' ', 'T')}:00Z`);
    if (Number.isNaN(ms)) return label;
    const next = new Date(ms + (TIMEFRAME_SECONDS[timeframe] || 300) * 1000);
    const day = `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
    return daily ? day : `${day} ${pad(next.getUTCHours())}:${pad(next.getUTCMinutes())}`;
};

const smallButtonSx = { fontFamily, textTransform: 'none', fontSize: '0.75rem', borderRadius: '8px' };
const sectionTitleSx = { fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#64748b' };
const fieldSx = {
    '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiFormHelperText-root': { fontFamily, fontSize: '0.68rem', mx: 0.5 },
    // The app's stylesheet makes every one-line input 1rem with 10px 15px padding.
    '& input.MuiInputBase-input': { fontSize: '0.8rem', padding: '8px 10px' },
};
const paperSx = { p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' };
const headCellSx = { fontFamily, fontSize: '0.72rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', py: 1 };
const bodyCellSx = { fontFamily, fontSize: '0.75rem', py: 0.75 };

// Plain inputs for the bar grid: a page is 25 rows of 13 cells, and an MUI input per cell costs
// far more to mount (see the Rule Editor's note on injected global styles).
const cellInputStyle = (error, shaded) => ({
    width: '100%', minWidth: 64, boxSizing: 'border-box', border: `1px solid ${error ? '#ef4444' : '#e2e8f0'}`,
    borderRadius: 6, padding: '4px 6px', fontFamily, fontSize: '0.75rem', textAlign: 'right',
    background: error ? '#fef2f2' : shaded ? '#f8fafc' : '#fff', color: '#1e293b', outline: 'none',
});

const StatusChip = ({ spec, label }) => (
    <Chip label={label || spec.label} size="small"
        sx={{ fontFamily, fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: spec.bg, color: spec.color }} />
);

// ==================== CHART ====================

// Module scope and memoised, like AIAgentPage's CandleChart: defined inside the component it
// would remount, and the chart would be rebuilt, on every render. `focus` ({from, to} bar indexes)
// is the part to show: a date of a multi-date scenario.
const SimChart = React.memo(({ bars, orders, steps, upto, height = 320, showPnl = false, focus = null }) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef({});

    useEffect(() => {
        if (!containerRef.current) return undefined;
        const chart = createChart(containerRef.current, {
            width: containerRef.current.clientWidth,
            height,
            layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: '#94a3b8', fontSize: 11 },
            grid: { vertLines: { color: '#f1f5f9' }, horzLines: { color: '#f1f5f9' } },
            rightPriceScale: { borderColor: '#e2e8f0' },
            timeScale: { borderColor: '#e2e8f0', timeVisible: true, secondsVisible: false },
            crosshair: { mode: 0 },
        });
        chartRef.current = chart;
        const quiet = { priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false, lineWidth: 1 };
        const candles = chart.addSeries(CandlestickSeries, {
            upColor: '#22c55e', downColor: '#ef4444', borderUpColor: '#22c55e', borderDownColor: '#ef4444',
            wickUpColor: '#22c55e', wickDownColor: '#ef4444',
        });
        const volume = chart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'volume' });
        chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
        const upper = chart.addSeries(LineSeries, { ...quiet, color: '#94a3b8', lineStyle: LineStyle.Dashed });
        const mid = chart.addSeries(LineSeries, { ...quiet, color: '#a78bfa' });
        const lower = chart.addSeries(LineSeries, { ...quiet, color: '#94a3b8', lineStyle: LineStyle.Dashed });
        const markers = createSeriesMarkers(candles, []);
        let pnl = null;
        if (showPnl) {
            pnl = chart.addSeries(LineSeries, {
                color: ACCENT, lineWidth: 2, priceScaleId: 'pnl', title: 'P&L',
                priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
            });
            chart.priceScale('pnl').applyOptions({ scaleMargins: { top: 0.72, bottom: 0.05 } });
        }
        seriesRef.current = { candles, volume, upper, mid, lower, markers, pnl };
        const resizeObserver = new ResizeObserver(() => {
            const width = containerRef.current?.clientWidth;
            if (width) chart.applyOptions({ width });
        });
        resizeObserver.observe(containerRef.current);
        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
            seriesRef.current = {};
        };
    }, [height, showPnl]);

    const focusFrom = focus ? focus.from : null;
    const focusTo = focus ? focus.to : null;

    useEffect(() => {
        const s = seriesRef.current;
        if (!s.candles) return;
        const last = upto === null || upto === undefined ? bars.length - 1 : Math.min(upto, bars.length - 1);
        // Strictly ascending times only: an unsaved edit can put two bars at one time, and
        // lightweight-charts throws on that.
        const shown = [];
        const timeOf = {};
        bars.slice(0, last + 1).forEach((bar, i) => {
            const t = toChartTime(bar.time);
            if (t === null || (shown.length && !(t > shown[shown.length - 1].t))) return;
            timeOf[i] = t;
            shown.push({ ...bar, t });
        });
        // A focused date the replay is inside of keeps its later bars as blank slots: the chart
        // holds the whole date, where it would otherwise clamp the frame to the bars drawn and then
        // slide it with every new one.
        const blanks = [];
        let prev = shown.length ? shown[shown.length - 1].t : null;
        bars.slice(last + 1, (focusTo ?? -1) + 1).forEach((bar) => {
            const t = toChartTime(bar.time);
            if (t === null || (prev !== null && !(t > prev))) return;
            blanks.push({ time: t });
            prev = t;
        });
        const precision = shown.some((b) => b.close < 1) ? 4 : 2;
        s.candles.applyOptions({ priceFormat: { type: 'price', precision, minMove: 1 / 10 ** precision } });
        s.candles.setData([...shown.map((b) => ({ time: b.t, open: b.open, high: b.high, low: b.low, close: b.close })), ...blanks]);
        s.volume.setData(shown.map((b) => ({
            time: b.t, value: b.volume || 0, color: b.close >= b.open ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
        })));
        const line = (key) => shown.filter((b) => b[key] !== null && b[key] !== undefined).map((b) => ({ time: b.t, value: b[key] }));
        s.upper.setData(line('vwap_upper'));
        s.mid.setData(line('vwap_mid'));
        s.lower.setData(line('vwap_lower'));
        const marks = [];
        (orders || []).forEach((o) => {
            if (o.start || o.bar > last || timeOf[o.bar] === undefined) return;
            const buy = o.side === 'BUY';
            marks.push({
                time: timeOf[o.bar], position: buy ? 'belowBar' : 'aboveBar', color: buy ? '#16a34a' : '#dc2626',
                shape: buy ? 'arrowUp' : 'arrowDown', text: `${o.action} ${fmtQty(o.qty)}`,
            });
            if (o.fill_bar !== null && o.fill_bar !== o.bar && o.fill_bar <= last && timeOf[o.fill_bar] !== undefined) {
                marks.push({ time: timeOf[o.fill_bar], position: 'inBar', color: buy ? '#16a34a' : '#dc2626', shape: 'circle', text: `#${o.id}` });
            }
        });
        marks.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        s.markers.setMarkers(marks);
        if (s.pnl) {
            s.pnl.setData((steps || []).filter((st) => st.i <= last && timeOf[st.i] !== undefined)
                .map((st) => ({ time: timeOf[st.i], value: st.pnl.total })));
        }
    }, [bars, orders, steps, upto, focusTo]);

    // Fit once per data set and focus, not on every replay tick: the focused date, else every bar
    // up to FIT_BARS, else the last FIT_BARS. A bar's logical index is its index (saved bars are
    // strictly ascending; only an unsaved duplicate time shifts it, by a bar).
    useEffect(() => {
        const scale = chartRef.current?.timeScale();
        if (!scale) return;
        if (focusFrom !== null && focusTo !== null) {
            scale.setVisibleLogicalRange({ from: focusFrom - 1, to: focusTo + 1 });
        } else if (bars.length > FIT_BARS) {
            scale.setVisibleLogicalRange({ from: bars.length - FIT_BARS, to: bars.length + 1 });
        } else {
            scale.fitContent();
        }
    }, [bars, height, showPnl, focusFrom, focusTo]);

    return <Box ref={containerRef} sx={{ width: '100%', minHeight: height }} />;
});

// ==================== BAR GRID ====================

const CellInput = ({ index, field, value, error, readOnly, shaded, onCommit, align }) => (
    <input
        // A new key when the stored value changes (a recalculation, a regenerate) remounts the
        // input with it; typing stays local until the cell is left.
        key={cellText(value)}
        defaultValue={cellText(value)}
        readOnly={readOnly}
        title={error || undefined}
        aria-label={`Bar ${index} ${field}`}
        onBlur={(e) => { if (e.target.value !== cellText(value)) onCommit(index, field, e.target.value, e.target); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{ ...cellInputStyle(error, shaded), textAlign: align || 'right' }}
    />
);

const BarRow = React.memo(({ index, bar, warmup, rowErrors, readOnly, onCommit, onDelete }) => {
    const shaded = index < warmup;
    return (
        <tr style={{ background: shaded ? '#f8fafc' : 'transparent' }}>
            <td style={{ padding: '2px 6px', fontFamily, fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {index}{shaded ? ' ·w' : ''}
            </td>
            <td style={{ padding: 2, minWidth: 138 }}>
                <CellInput index={index} field="time" value={bar.time} error={rowErrors?.time} readOnly={readOnly}
                    shaded={shaded} onCommit={onCommit} align="left" />
            </td>
            {BAR_COLUMNS.map((col) => (
                <td key={col.key} style={{ padding: 2 }}>
                    <CellInput index={index} field={col.key} value={bar[col.key]} error={rowErrors?.[col.key]}
                        readOnly={readOnly} shaded={shaded} onCommit={onCommit} />
                </td>
            ))}
            <td style={{ padding: 2 }}>
                {!readOnly && (
                    <IconButton size="small" aria-label={`Delete bar ${index}`} onClick={() => onDelete(index)}
                        sx={{ p: 0.3, color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}>
                        <MuiIcons.DeleteOutline sx={{ fontSize: '0.95rem' }} />
                    </IconButton>
                )}
            </td>
        </tr>
    );
});

// ==================== MARKET CONTEXT ====================

// One context field. Numbers are typed text, committed when the box is left; blank is N/A.
const ContextInput = ({ field, value, error, readOnly, onChange }) => {
    if (field.kind === 'flag') {
        return (
            <FormControlLabel
                control={<Switch size="small" checked={Boolean(value)} disabled={readOnly}
                    onChange={(e) => onChange(field.name, e.target.checked)} />}
                label={<Typography sx={{ fontFamily, fontSize: '0.75rem' }}>{field.label}</Typography>}
            />
        );
    }
    if (field.kind === 'text') {
        return (
            <FormControl size="small" fullWidth sx={fieldSx} error={Boolean(error)}>
                <InputLabel>{field.label}</InputLabel>
                <Select label={field.label} value={value ?? ''} disabled={readOnly}
                    onChange={(e) => onChange(field.name, e.target.value === '' ? null : e.target.value)}>
                    <MenuItem value=""><em>N/A</em></MenuItem>
                    {(field.values || []).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
            </FormControl>
        );
    }
    return (
        <TextField
            key={cellText(value)}
            size="small" fullWidth label={field.label} placeholder="N/A" defaultValue={cellText(value)}
            disabled={readOnly} error={Boolean(error)} helperText={error || undefined}
            onBlur={(e) => {
                const parsed = parseCell(e.target.value, true);
                if (parsed === undefined) {
                    e.target.value = cellText(value);
                    return;
                }
                if (parsed !== value) onChange(field.name, parsed);
            }}
            sx={fieldSx}
        />
    );
};

// ==================== RULE TRACE ====================

const TraceView = ({ trace }) => {
    if (!trace) return null;
    if (trace.valid === false) {
        return <Alert severity="error" sx={{ fontFamily, fontSize: '0.8rem' }}>{trace.errors?.[0]?.message || 'The rule is not valid.'}</Alert>;
    }
    return (
        <Box>
            {trace.values?.length > 0 && (
                <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {trace.values.map((v) => (
                        <Chip key={v.name} size="small" variant="outlined"
                            label={`${v.name} = ${v.missing ? 'N/A' : traceValue(v.value)}`}
                            sx={{ fontFamily, fontSize: '0.7rem', height: 22 }} />
                    ))}
                </Box>
            )}
            {(trace.steps || []).map((step) => {
                const status = TRACE_STATUS[step.status] || TRACE_STATUS.NOT_MET;
                return (
                    <Box key={step.index} sx={{ mb: 1, p: 1, borderRadius: '8px', border: '1px solid #e2e8f0', bgcolor: step.status === 'FIRED' ? '#f0fdf4' : '#fff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600 }}>
                                {step.index + 1}. {step.name}
                            </Typography>
                            <Chip size="small" label={step.action} sx={{ fontFamily, fontSize: '0.65rem', height: 18 }} />
                            <StatusChip spec={status} />
                        </Box>
                        {step.detail && (
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#b45309', mt: 0.5 }}>{step.detail}</Typography>
                        )}
                        {(step.conditions || []).map((c, n) => (
                            <Typography key={n} sx={{ fontFamily, fontSize: '0.72rem', color: '#475569', mt: 0.3 }}>
                                <span style={{ color: CONDITION_COLORS[c.result] || '#64748b', fontWeight: 700, marginRight: 6 }}>{c.result}</span>
                                {c.left} ({traceValue(c.left_value)}) {c.op} {c.right} ({traceValue(c.right_value)})
                            </Typography>
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

// ==================== THE TAB ====================

const AIAgentDataSimulator = ({ agent, variant = 'single', canAdd, canEdit, canDelete, onNotify, renderSnapshot }) => {
    // `multiDate`: the AI Agent Simulator page's scenarios, a range of dates each, on its routes.
    const multi = variant === 'multiDate';
    const api = multi ? `/api/trade-execution/ai-agent-simulator/${agent.id}` : `/api/trade-execution/ai-agents/${agent.id}/simulator`;

    const [scenarios, setScenarios] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [engines, setEngines] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [scenario, setScenario] = useState(null);
    const [scenarioLoading, setScenarioLoading] = useState(false);
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [view, setView] = useState('data');
    const [barPage, setBarPage] = useState(0);
    const [barDate, setBarDate] = useState('');            // Go to date: the date the Bars chart shows

    const [generator, setGenerator] = useState(null);      // the open generator dialog's form
    const [details, setDetails] = useState(null);          // the open name / notes dialog's form

    const [run, setRun] = useState(null);
    const [running, setRunning] = useState(false);
    const [runSettings, setRunSettings] = useState({ engine: 'ACTIVE', fill_model: 'INSTANT', start_bar: '', end_bar: '' });
    const [cursor, setCursor] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(2);
    const [stepPage, setStepPage] = useState(0);
    const [inspect, setInspect] = useState(null);          // {bar, loading, data, error}

    const stopRef = useRef(false);
    const mountedRef = useRef(true);
    const selectedIdRef = useRef(null);

    useEffect(() => () => { mountedRef.current = false; stopRef.current = true; }, []);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

    const failed = useCallback((err, fallback) => {
        onNotify({ message: apiMessage(err, fallback), severity: 'error' });
    }, [onNotify]);

    const fractional = Boolean(catalog?.defaults?.fractional);
    const readOnly = !canEdit || running;
    const steps = useMemo(() => run?.steps || [], [run]);
    const savedBars = scenario?.data?.bars || [];
    const currentStep = steps.length ? steps[Math.min(cursor, steps.length - 1)] : null;
    const currentSession = currentStep ? sessionOf(currentStep) : null;

    // The draft's bars by date, for Go to date: [{date, from, to}] in order.
    const draftDates = useMemo(() => {
        const out = [];
        (draft?.bars || []).forEach((bar, i) => {
            const day = String(bar.time).slice(0, 10);
            if (out.length && out[out.length - 1].date === day) out[out.length - 1].to = i;
            else out.push({ date: day, from: i, to: i });
        });
        return out;
    }, [draft]);
    // Found again after every edit, so a deleted bar moves the chart with the date.
    const barFocus = useMemo(() => draftDates.find((d) => d.date === barDate) || null, [draftDates, barDate]);

    // A multi-date replay shows the day the cursor is on, and moves to the next day with it.
    const replayFocus = useMemo(() => {
        if (!multi || !currentSession) return null;
        const day = steps.filter((s) => sessionOf(s) === currentSession);
        return day.length ? { from: day[0].i, to: day[day.length - 1].i } : null;
    }, [multi, steps, currentSession]);

    // ==================== LOADING ====================

    const applyScenario = useCallback((payload) => {
        setScenario(payload);
        setDraft(clone(payload.data));
        setDirty(false);
        setErrors({});
        setRun(payload.run || null);
        setCursor(Math.max((payload.run?.steps?.length || 1) - 1, 0));
        setBarPage(0);
        setBarDate('');
        setStepPage(0);
        setScenarios((list) => list.map((item) => (item.id === payload.id ? {
            ...item, name: payload.name, notes: payload.notes, bar_count: payload.bar_count, timeframe: payload.timeframe,
            run_status: payload.run_status, run_summary: payload.run_summary, run_version: payload.run_version,
            updated_at: payload.updated_at,
            // A regenerate can change the dates.
            date_from: payload.date_from, date_to: payload.date_to, date_count: payload.date_count,
        } : item)));
    }, []);

    const openScenario = useCallback(async (id) => {
        setSelectedId(id);
        setScenarioLoading(true);
        setInspect(null);
        setPlaying(false);
        try {
            const res = await axiosInstance.get(`${api}/scenarios/${id}`);
            if (!mountedRef.current || selectedIdRef.current !== id) return;
            applyScenario(res.data.data);
        } catch (err) {
            failed(err, 'Failed to load the scenario');
        } finally {
            if (mountedRef.current) setScenarioLoading(false);
        }
    }, [api, applyScenario, failed]);

    const loadList = useCallback(async (selectFirst) => {
        setListLoading(true);
        try {
            const res = await axiosInstance.get(`${api}/scenarios`);
            if (!mountedRef.current) return;
            setScenarios(res.data.data || []);
            setCatalog(res.data.catalog || null);
            setEngines(res.data.engines || []);
            if (selectFirst && res.data.data?.length) openScenario(res.data.data[0].id);
        } catch (err) {
            failed(err, 'Failed to load the scenarios');
        } finally {
            if (mountedRef.current) setListLoading(false);
        }
    }, [api, failed, openScenario]);

    useEffect(() => { loadList(true); }, [loadList]);

    const selectScenario = (id) => {
        if (id === selectedId || running) return;
        if (dirty && !window.confirm(DISCARD_CHANGES)) return;
        openScenario(id);
    };

    // ==================== SCENARIO ACTIONS ====================

    const openGenerator = (mode) => {
        const name = mode === 'create' ? `Scenario ${scenarios.length + 1}` : scenario?.name;
        if (multi) {
            setGenerator({
                ...multiDateForm(mode === 'regenerate' ? scenario?.generator : null, catalog, fractional, mode === 'regenerate'),
                mode, name,
            });
            return;
        }
        const defaults = catalog?.defaults || {};
        const params = mode === 'regenerate' ? (scenario?.generator || {}) : {};
        setGenerator({
            mode,
            name,
            pattern: params.pattern || 'DIP_AND_RECOVERY',
            bars: params.bars ?? defaults.bars ?? 100,
            warmup: params.warmup ?? defaults.warmup ?? 20,
            timeframe: params.timeframe || defaults.timeframe || 'FIVE_MIN',
            start_date: params.start_date || '',
            start_price: params.start_price ?? '',
            volatility_pct: params.volatility_pct ?? '',
            trend_pct: params.trend_pct ?? 0,
            volume: params.volume ?? '',
            // A regenerate keeps the seed, so changing one setting changes only what it should.
            seed: mode === 'regenerate' ? (params.seed ?? '') : '',
            options_levels: params.options_levels || (fractional ? 'NONE' : 'AUTO'),
            use_live_context: params.use_live_context ?? true,
            errors: {},
        });
    };

    const generatorBody = (form) => {
        const number = (value) => (value === '' || value === null || value === undefined ? null : Number(value));
        return {
            pattern: form.pattern, bars: number(form.bars), warmup: number(form.warmup), timeframe: form.timeframe,
            start_date: form.start_date || null, start_price: number(form.start_price),
            volatility_pct: number(form.volatility_pct), trend_pct: number(form.trend_pct), volume: number(form.volume),
            seed: number(form.seed), options_levels: form.options_levels, use_live_context: Boolean(form.use_live_context),
        };
    };

    const submitGenerator = async () => {
        const form = generator;
        if (form.mode === 'regenerate' && !window.confirm(
            'Regenerate this scenario? Its bars, market context and every edit are replaced, and its run is cleared.')) return;
        setBusy(true);
        try {
            const body = form.multi ? multiDateBody(form) : generatorBody(form);
            const res = form.mode === 'create'
                ? await axiosInstance.post(`${api}/scenarios`, { name: form.name, generator: body })
                : await axiosInstance.post(`${api}/scenarios/${scenario.id}/generate`, {
                    generator: body, version: scenario.run_version,
                });
            const payload = res.data.data;
            if (form.mode === 'create') {
                setScenarios((list) => [payload, ...list]);
                setSelectedId(payload.id);
                selectedIdRef.current = payload.id;
            }
            applyScenario(payload);
            setGenerator(null);
            setView('data');
            (res.data.warnings || []).forEach((message) => onNotify({ message, severity: 'warning' }));
            onNotify({ message: res.data.message || 'Scenario ready', severity: 'success' });
        } catch (err) {
            const list = err.response?.data?.errors;
            if (list) {
                setGenerator((g) => ({ ...g, errors: errorsByPath(list.map((e) => ({ ...e, path: e.path.replace(/^generator\./, '') }))) }));
            }
            if (err.response?.status === 409) openScenario(scenario.id);
            failed(err, 'Failed to generate the scenario');
        } finally {
            setBusy(false);
        }
    };

    const duplicateScenario = async (item) => {
        setBusy(true);
        try {
            const res = await axiosInstance.post(`${api}/scenarios`, { name: `${item.name} (copy)`.slice(0, 120), copy_from: item.id });
            setScenarios((list) => [res.data.data, ...list]);
            onNotify({ message: 'Scenario copied', severity: 'success' });
        } catch (err) {
            failed(err, 'Failed to copy the scenario');
        } finally {
            setBusy(false);
        }
    };

    const deleteScenario = async (item) => {
        if (!window.confirm(`Delete the scenario "${item.name}" and its run?`)) return;
        setBusy(true);
        try {
            await axiosInstance.delete(`${api}/scenarios/${item.id}`);
            setScenarios((list) => list.filter((s) => s.id !== item.id));
            if (item.id === selectedId) {
                setSelectedId(null);
                setScenario(null);
                setDraft(null);
                setRun(null);
                setDirty(false);
            }
            onNotify({ message: 'Scenario deleted', severity: 'success' });
        } catch (err) {
            failed(err, 'Failed to delete the scenario');
        } finally {
            setBusy(false);
        }
    };

    const saveDetails = async () => {
        setBusy(true);
        try {
            const res = await axiosInstance.put(`${api}/scenarios/${scenario.id}`, { name: details.name, notes: details.notes });
            const payload = res.data.data;
            setScenario((s) => ({ ...s, name: payload.name, notes: payload.notes }));
            setScenarios((list) => list.map((s) => (s.id === payload.id ? { ...s, name: payload.name, notes: payload.notes } : s)));
            setDetails(null);
        } catch (err) {
            failed(err, 'Failed to save the name');
        } finally {
            setBusy(false);
        }
    };

    const saveData = async () => {
        setBusy(true);
        try {
            const res = await axiosInstance.put(`${api}/scenarios/${scenario.id}`, { data: draft, version: scenario.run_version });
            applyScenario(res.data.data);
            onNotify({ message: 'Scenario saved. Its run was cleared: run it again on the new data.', severity: 'success' });
        } catch (err) {
            const list = err.response?.data?.errors;
            if (list) setErrors(errorsByPath(list));
            if (err.response?.status === 409) {
                onNotify({ message: apiMessage(err, 'This scenario changed elsewhere.'), severity: 'error' });
            } else {
                failed(err, 'Failed to save the scenario');
            }
        } finally {
            setBusy(false);
        }
    };

    const discardDraft = () => {
        if (!window.confirm(DISCARD_CHANGES)) return;
        setDraft(clone(scenario.data));
        setDirty(false);
        setErrors({});
    };

    const recalculate = async () => {
        setBusy(true);
        try {
            const res = await axiosInstance.post(`${api}/indicators`, { timeframe: draft.timeframe, bars: draft.bars });
            setDraft((d) => ({ ...d, bars: res.data.data.bars }));
            setDirty(true);
            setErrors({});
            onNotify({ message: 'Indicators worked out again from the prices. Save to keep them.', severity: 'success' });
        } catch (err) {
            const list = err.response?.data?.errors;
            if (list) setErrors(errorsByPath(list));
            failed(err, 'Failed to recalculate the indicators');
        } finally {
            setBusy(false);
        }
    };

    // ==================== DRAFT EDITS ====================

    const editDraft = useCallback((change) => {
        setDraft((d) => change(d));
        setDirty(true);
    }, []);

    // A cell left with new text. High and Low widen to hold an edited Open or Close, so the
    // bar stays a bar; anything that is not a number puts the old value back.
    const commitCell = useCallback((index, field, text, input) => {
        const column = BAR_COLUMNS.find((c) => c.key === field);
        let value = text.trim();
        if (field !== 'time') {
            value = parseCell(text, column?.optional);
            if (value === undefined) {
                input.value = input.defaultValue;
                onNotify({ message: `Bar ${index}: ${column?.label || field} must be a number${column?.optional ? ' or blank' : ''}.`, severity: 'warning' });
                return;
            }
        }
        editDraft((d) => {
            const bars = d.bars.slice();
            const bar = { ...bars[index], [field]: value };
            if (field === 'open' || field === 'close') {
                if (bar.high !== null && value > bar.high) bar.high = value;
                if (bar.low !== null && value < bar.low) bar.low = value;
            }
            bars[index] = bar;
            return { ...d, bars };
        });
    }, [editDraft, onNotify]);

    // A deleted bar shifts the context changes and the warm-up after it. Errors are kept by
    // bar number, so they would point at the wrong bars: they go until the next save.
    const deleteBar = useCallback((index) => {
        editDraft((d) => ({
            ...d,
            bars: d.bars.filter((_, i) => i !== index),
            events: (d.events || []).map((e) => (e.bar > index ? { ...e, bar: e.bar - 1 } : e)),
            warmup: d.warmup > index ? d.warmup - 1 : d.warmup,
        }));
        setErrors({});
    }, [editDraft]);

    const appendBar = () => {
        editDraft((d) => {
            const last = d.bars[d.bars.length - 1];
            const close = last.close;
            return {
                ...d,
                bars: [...d.bars, {
                    time: nextBarLabel(last.time, d.timeframe), open: close, high: close, low: close, close, volume: last.volume,
                    vwap_upper: null, vwap_mid: null, vwap_lower: null, atr: null, cmf: null, bbw: null, price: null,
                }],
            };
        });
        const pages = Math.ceil((draft.bars.length + 1) / BARS_PER_PAGE);
        setBarPage(pages - 1);
    };

    const setContextField = useCallback((name, value) => {
        editDraft((d) => ({ ...d, context: { ...d.context, [name]: value } }));
    }, [editDraft]);

    const rowErrors = useMemo(() => {
        const out = {};
        Object.entries(errors).forEach(([path, message]) => {
            const match = /^bars\[(\d+)\]\.(\w+)$/.exec(path);
            if (match) {
                out[match[1]] = { ...(out[match[1]] || {}), [match[2]]: message };
            }
        });
        return out;
    }, [errors]);

    // ==================== RUN ====================

    const engineChoice = engines.find((e) => e.key === runSettings.engine) || engines[0];
    const engineKind = engineChoice?.kind;

    const runBody = () => {
        const options = { fill_model: runSettings.fill_model };
        if (runSettings.start_bar !== '') options.start_bar = Number(runSettings.start_bar);
        if (runSettings.end_bar !== '') options.end_bar = Number(runSettings.end_bar);
        return { engine: runSettings.engine, options };
    };

    const startRun = async (mode) => {
        if (dirty) {
            onNotify({ message: 'Save your changes first: a run uses the saved scenario.', severity: 'warning' });
            return;
        }
        const id = scenario.id;
        let version = scenario.run_version;
        let body = mode === 'start' ? { ...runBody(), mode } : { mode };
        if (mode === 'start' && engineKind === 'LLM') {
            const start = runSettings.start_bar === '' ? (draft.warmup || 0) : Number(runSettings.start_bar);
            const end = runSettings.end_bar === '' ? draft.bars.length - 1 : Number(runSettings.end_bar);
            // The server refuses a longer LLM run; say how to fit it before asking to pay for it.
            const cap = catalog?.limits?.max_llm_steps || 100;
            if (end - start + 1 > cap) {
                onNotify({
                    message: `An LLM run takes at most ${cap} bars, and this one is ${end - start + 1}. Set From bar and To bar `
                        + `(for example ${start} to ${start + cap - 1}).`,
                    severity: 'warning',
                });
                return;
            }
            if (!window.confirm(`This run calls Gemini once a bar: up to ${end - start + 1} calls, billed like live cycles. Start it?`)) return;
            body = { ...body, confirm_llm: true };
        }
        stopRef.current = false;
        setRunning(true);
        setPlaying(false);
        setInspect(null);
        if (mode === 'start') {
            setRun(null);
            setCursor(0);
            setStepPage(0);
        }
        try {
            let first = true;
            // How many bars the run holds, so the replay can follow the newest one.
            let count = mode === 'start' ? 0 : (run?.steps?.length || 0);
            for (;;) {
                // eslint-disable-next-line no-await-in-loop
                const res = await axiosInstance.post(`${api}/scenarios/${id}/run`, { ...body, version });
                if (!mountedRef.current || selectedIdRef.current !== id) return;
                const data = res.data.data;
                version = data.run_version;
                const replace = mode === 'start' && first;
                first = false;
                count += data.steps.length;
                setRun((prev) => ({ ...data.run, steps: replace ? data.steps : [...(prev?.steps || []), ...data.steps] }));
                setCursor(Math.max(count - 1, 0));
                setScenario((s) => ({ ...s, run_version: data.run_version, run_status: data.run_status, run_summary: data.run.summary }));
                setScenarios((list) => list.map((s) => (s.id === id ? {
                    ...s, run_version: data.run_version, run_status: data.run_status, run_summary: data.run.summary,
                } : s)));
                if (data.run_status !== 'PAUSED' || stopRef.current) break;
                body = { mode: 'continue' };
            }
        } catch (err) {
            if (err.response?.status === 409) {
                onNotify({ message: apiMessage(err, 'This scenario changed elsewhere.'), severity: 'error' });
                openScenario(id);
            } else {
                failed(err, 'The run failed');
            }
        } finally {
            if (mountedRef.current) setRunning(false);
        }
    };

    // Replay: the cursor walks the run's bars at the chosen speed.
    useEffect(() => {
        if (!playing) return undefined;
        const timer = setInterval(() => {
            setCursor((c) => {
                if (c >= steps.length - 1) {
                    setPlaying(false);
                    return c;
                }
                return c + 1;
            });
        }, 1000 / speed);
        return () => clearInterval(timer);
    }, [playing, speed, steps.length]);

    // The steps table follows the cursor.
    useEffect(() => {
        if (steps.length) setStepPage(Math.floor(Math.min(cursor, steps.length - 1) / STEPS_PER_PAGE));
    }, [cursor, steps.length]);

    const openInspect = async (step) => {
        if (step.status === 'CLOSED') return;
        const bar = step.i;
        setInspect({ bar, loading: true, data: null, error: null });
        try {
            const res = await axiosInstance.get(`${api}/scenarios/${scenario.id}/steps/${bar}`);
            if (mountedRef.current) setInspect((cur) => (cur && cur.bar === bar ? { bar, loading: false, data: res.data.data, error: null } : cur));
        } catch (err) {
            if (mountedRef.current) setInspect((cur) => (cur && cur.bar === bar ? { bar, loading: false, data: null, error: apiMessage(err, 'Failed to load the bar') } : cur));
        }
    };

    // ==================== RENDER: LIST ====================

    const renderList = () => (
        <Paper sx={{ width: 280, flexShrink: 0, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'auto', alignSelf: 'flex-start' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Scenarios</Typography>
                {canAdd && (
                    <Tooltip title="New scenario">
                        <span>
                            <IconButton size="small" aria-label="New scenario" disabled={busy || running || !catalog}
                                onClick={() => { if (!dirty || window.confirm(DISCARD_CHANGES)) openGenerator('create'); }}>
                                <MuiIcons.Add fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
            {listLoading && scenarios.length === 0 && <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '8px' }} />}
            {!listLoading && scenarios.length === 0 && (
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>No scenarios yet.</Typography>
            )}
            {scenarios.map((item) => {
                const isSelected = item.id === selectedId;
                const status = RUN_STATUS[item.run_status];
                const total = item.run_summary?.total_pnl;
                return (
                    <Paper key={item.id} onClick={() => selectScenario(item.id)}
                        sx={{
                            p: 1.5, mb: 1, cursor: running ? 'default' : 'pointer', borderRadius: '8px',
                            border: `1px solid ${isSelected ? ACCENT : '#e2e8f0'}`,
                            bgcolor: isSelected ? `${ACCENT}08` : 'transparent',
                            '&:hover': { borderColor: ACCENT },
                        }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, flex: 1, wordBreak: 'break-word' }}>{item.name}</Typography>
                            <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                {canAdd && (
                                    <Tooltip title="Duplicate scenario">
                                        <span>
                                            <IconButton size="small" disabled={busy || running} aria-label={`Duplicate ${item.name}`}
                                                onClick={(e) => { e.stopPropagation(); duplicateScenario(item); }}
                                                sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: ACCENT } }}>
                                                <MuiIcons.ContentCopy sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {canDelete && (
                                    <Tooltip title="Delete scenario">
                                        <span>
                                            <IconButton size="small" disabled={busy || running} aria-label={`Delete ${item.name}`}
                                                onClick={(e) => { e.stopPropagation(); deleteScenario(item); }}
                                                sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                <MuiIcons.DeleteOutline sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        {multi && item.date_count > 0 && (
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#475569', mt: 0.3 }}>
                                {item.date_count} date{item.date_count === 1 ? '' : 's'} · {item.date_from} to {item.date_to}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mt: 0.3 }}>
                            {item.bar_count} bars · {TimeFrameLabels[item.timeframe] || item.timeframe}
                        </Typography>
                        {status && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                <StatusChip spec={status} />
                                {total !== undefined && total !== null && (
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 600, color: pnlColor(total) }}>{fmtMoney(total)}</Typography>
                                )}
                            </Box>
                        )}
                    </Paper>
                );
            })}
        </Paper>
    );

    // ==================== RENDER: DATA ====================

    // Go to date: the Bars chart shows that date and the grid opens on its page.
    const goToDate = (date) => {
        setBarDate(date);
        const entry = draftDates.find((d) => d.date === date);
        if (entry) setBarPage(Math.floor(entry.from / BARS_PER_PAGE));
    };

    const renderData = () => {
        const bars = draft.bars;
        const pageBars = bars.slice(barPage * BARS_PER_PAGE, (barPage + 1) * BARS_PER_PAGE);
        return (
            <>
                <Paper sx={{ ...paperSx, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                            Bars
                            <Typography component="span" sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', ml: 1 }}>
                                {TimeFrameLabels[draft.timeframe] || draft.timeframe} · {agent.trading_timezone || 'America/Chicago'} · VWAP bands dashed
                            </Typography>
                        </Typography>
                        {draftDates.length > 1 && (
                            <FormControl size="small" sx={{ ...fieldSx, width: 210 }}>
                                <InputLabel>Go to date</InputLabel>
                                <Select label="Go to date" value={barFocus ? barDate : ''} onChange={(e) => goToDate(e.target.value)}
                                    renderValue={(date) => (date ? `${date} ${weekdayOf(date)}` : '')}>
                                    <MenuItem value="" sx={{ fontFamily, fontSize: '0.8rem' }}><em>{bars.length > FIT_BARS ? `The last ${FIT_BARS} bars` : 'Every bar'}</em></MenuItem>
                                    {draftDates.map((d) => (
                                        <MenuItem key={d.date} value={d.date} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                            {d.date} {weekdayOf(d.date)} · bars {d.from} to {d.to}{d.to < (draft.warmup || 0) ? ' (warm-up)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                    <SimChart bars={bars} height={280} focus={barFocus} />
                </Paper>

                <Paper sx={{ ...paperSx }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        <TextField size="small" label="Warm-up bars" type="number" value={draft.warmup ?? 0} disabled={readOnly}
                            onChange={(e) => editDraft((d) => ({ ...d, warmup: Math.max(0, Math.min(Number(e.target.value) || 0, d.bars.length - 1)) }))}
                            helperText="History only: the agent decides from the bar after them" sx={{ ...fieldSx, width: 220 }}
                            error={Boolean(errors.warmup)} />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Work out VWAP bands, ATR, CMF and BBW again from the prices, the way the live indicators do. Replaces the indicator columns.">
                            <span>
                                <Button size="small" variant="outlined" disabled={readOnly || busy} onClick={recalculate}
                                    startIcon={<MuiIcons.Calculate sx={{ fontSize: '1rem' }} />} sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                                    Recalculate indicators
                                </Button>
                            </span>
                        </Tooltip>
                        <Button size="small" variant="outlined" disabled={readOnly || busy || bars.length >= (catalog?.limits?.max_bars || 500)} onClick={appendBar}
                            startIcon={<MuiIcons.Add sx={{ fontSize: '1rem' }} />} sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                            Add bar
                        </Button>
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mb: 1 }}>
                        Edit any cell and leave it to apply. High and Low widen to hold an edited Open or Close. Blank indicator cells read
                        as not available. Shaded rows (·w) are the warm-up.
                    </Typography>
                    {errors.bars && <Alert severity="error" sx={{ fontFamily, fontSize: '0.78rem', mb: 1 }}>{errors.bars}</Alert>}
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '4px 6px', fontFamily, fontWeight: 600, fontSize: '0.7rem', color: '#64748b' }}>#</th>
                                    <th style={{ textAlign: 'left', padding: '4px 6px', fontFamily, fontWeight: 600, fontSize: '0.7rem', color: '#64748b' }}>Time</th>
                                    {BAR_COLUMNS.map((col) => (
                                        <th key={col.key} title={col.hint || undefined}
                                            style={{ textAlign: 'right', padding: '4px 6px', fontFamily, fontWeight: 600, fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                                            {col.label}
                                        </th>
                                    ))}
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {pageBars.map((bar, offset) => {
                                    const index = barPage * BARS_PER_PAGE + offset;
                                    return (
                                        <BarRow key={index} index={index} bar={bar} warmup={draft.warmup || 0}
                                            rowErrors={rowErrors[index]} readOnly={readOnly} onCommit={commitCell} onDelete={deleteBar} />
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                    <TablePagination component="div" count={bars.length} page={barPage} rowsPerPage={BARS_PER_PAGE}
                        rowsPerPageOptions={[BARS_PER_PAGE]} onPageChange={(_, p) => setBarPage(p)}
                        sx={{ fontFamily, '& .MuiTablePagination-displayedRows': { fontFamily } }} />
                </Paper>
            </>
        );
    };

    // ==================== RENDER: MARKET CONTEXT ====================

    const contextFields = useMemo(() => (catalog?.context_fields || []).flatMap((g) => g.fields), [catalog]);

    const renderEvents = () => {
        const events = draft.events || [];
        const setEvent = (n, change) => editDraft((d) => ({ ...d, events: d.events.map((e, i) => (i === n ? { ...e, ...change } : e)) }));
        return (
            <Paper sx={{ ...paperSx, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>Context changes</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                            From a bar on, a field takes a new value: the Put Wall moves, SPY drops 2%, earnings land.
                        </Typography>
                    </Box>
                    <Button size="small" disabled={readOnly || events.length >= (catalog?.limits?.max_events || 100) || !contextFields.length}
                        startIcon={<MuiIcons.Add sx={{ fontSize: '1rem' }} />} sx={{ ...smallButtonSx, color: ACCENT }}
                        onClick={() => editDraft((d) => ({
                            ...d, events: [...(d.events || []), { bar: d.warmup || 0, field: contextFields[0].name, value: d.context[contextFields[0].name] ?? null }],
                        }))}>
                        Add change
                    </Button>
                </Box>
                {events.length === 0 && <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>No changes: the context holds for every bar.</Typography>}
                {events.map((event, n) => {
                    const field = contextFields.find((f) => f.name === event.field) || contextFields[0];
                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box key={n} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                            <TextField size="small" label="From bar" type="number" value={event.bar ?? ''} disabled={readOnly}
                                onChange={(e) => setEvent(n, { bar: e.target.value === '' ? null : Number(e.target.value) })}
                                error={Boolean(errors[`events[${n}].bar`])} helperText={errors[`events[${n}].bar`] || undefined}
                                sx={{ ...fieldSx, width: 110 }} />
                            <FormControl size="small" sx={{ ...fieldSx, width: 260 }}>
                                <InputLabel>Field</InputLabel>
                                <Select label="Field" value={event.field} disabled={readOnly}
                                    onChange={(e) => setEvent(n, { field: e.target.value, value: draft.context[e.target.value] ?? null })}>
                                    {contextFields.map((f) => <MenuItem key={f.name} value={f.name} sx={{ fontFamily, fontSize: '0.8rem' }}>{f.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Box sx={{ flex: 1, minWidth: 160 }}>
                                <ContextInput field={{ ...field, label: 'Value' }} value={event.value} readOnly={readOnly}
                                    error={errors[`events[${n}].value`]} onChange={(_, value) => setEvent(n, { value })} />
                            </Box>
                            <IconButton size="small" disabled={readOnly} aria-label="Remove change"
                                onClick={() => editDraft((d) => ({ ...d, events: d.events.filter((_, i) => i !== n) }))}
                                sx={{ mt: 0.5, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                <MuiIcons.DeleteOutline sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Paper>
        );
    };

    const renderContext = () => {
        const start = draft.start_position || { side: 'NONE' };
        const setStart = (change) => editDraft((d) => ({ ...d, start_position: { ...(d.start_position || {}), ...change } }));
        const number = (value) => (value === '' ? null : Number(value));
        return (
            <>
                <Paper sx={{ ...paperSx, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>Account</Typography>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={3}>
                            <TextField size="small" fullWidth label="Capital" type="number" value={draft.capital ?? ''} disabled={readOnly}
                                placeholder={String(agent.capital_allocation ?? '')}
                                helperText={`Blank: the agent's Capital Allocation (${fmtMoney(agent.capital_allocation || 0)})`}
                                error={Boolean(errors.capital)}
                                onChange={(e) => editDraft((d) => ({ ...d, capital: number(e.target.value) }))} sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl size="small" fullWidth sx={fieldSx}>
                                <InputLabel>Starting position</InputLabel>
                                <Select label="Starting position" value={start.side || 'NONE'} disabled={readOnly}
                                    onChange={(e) => setStart(e.target.value === 'NONE'
                                        ? { side: 'NONE', quantity: 0, avg_cost: null, entries: 0 }
                                        : { side: e.target.value, quantity: start.quantity || 1, avg_cost: start.avg_cost || savedBars[0]?.open || null, entries: start.entries || 1 })}>
                                    <MenuItem value="NONE">Flat</MenuItem>
                                    <MenuItem value="LONG">Long</MenuItem>
                                    {!fractional && <MenuItem value="SHORT">Short</MenuItem>}
                                </Select>
                            </FormControl>
                        </Grid>
                        {start.side && start.side !== 'NONE' && (
                            <>
                                <Grid item xs={4} md={2}>
                                    <TextField size="small" fullWidth label="Quantity" type="number" value={start.quantity ?? ''} disabled={readOnly}
                                        error={Boolean(errors['start_position.quantity'])} helperText={errors['start_position.quantity'] || undefined}
                                        onChange={(e) => setStart({ quantity: number(e.target.value) })} sx={fieldSx} />
                                </Grid>
                                <Grid item xs={4} md={2}>
                                    <TextField size="small" fullWidth label="Average cost" type="number" value={start.avg_cost ?? ''} disabled={readOnly}
                                        error={Boolean(errors['start_position.avg_cost'])} helperText={errors['start_position.avg_cost'] || undefined}
                                        onChange={(e) => setStart({ avg_cost: number(e.target.value) })} sx={fieldSx} />
                                </Grid>
                                <Grid item xs={4} md={2}>
                                    <FormControl size="small" fullWidth sx={fieldSx}>
                                        <InputLabel>Ladder depth</InputLabel>
                                        <Select label="Ladder depth" value={start.entries || 1} disabled={readOnly}
                                            onChange={(e) => setStart({ entries: e.target.value })}>
                                            {[1, 2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n} (cycle_trades)</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                    </Grid>
                    {errors['start_position.side'] && <Alert severity="error" sx={{ fontFamily, fontSize: '0.78rem', mt: 1 }}>{errors['start_position.side']}</Alert>}
                </Paper>

                {renderEvents()}

                {(catalog?.context_fields || []).map((group) => (
                    <Paper key={group.group} sx={{ ...paperSx, mb: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>{group.group}</Typography>
                        <Grid container spacing={1.5}>
                            {group.fields.map((field) => (
                                <Grid item xs={12} sm={6} md={field.kind === 'flag' ? 6 : 3} key={field.name}>
                                    <ContextInput field={field} value={draft.context?.[field.name]} readOnly={readOnly}
                                        error={errors[`context.${field.name}`]} onChange={setContextField} />
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                ))}
            </>
        );
    };

    // ==================== RENDER: RUN & RESULTS ====================

    const renderRunSettings = () => {
        const lastBar = savedBars.length - 1;
        return (
            <Paper sx={{ ...paperSx, mb: 2 }}>
                <Grid container spacing={1.5} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                            <InputLabel>Decides with</InputLabel>
                            <Select label="Decides with" value={engineChoice?.key || 'ACTIVE'} disabled={running}
                                onChange={(e) => setRunSettings((r) => ({ ...r, engine: e.target.value }))}>
                                {engines.map((e) => <MenuItem key={e.key} value={e.key} sx={{ fontFamily, fontSize: '0.8rem' }}>{e.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                            <InputLabel>Fills</InputLabel>
                            <Select label="Fills" value={runSettings.fill_model} disabled={running}
                                onChange={(e) => setRunSettings((r) => ({ ...r, fill_model: e.target.value }))}>
                                {(catalog?.fill_models || ['INSTANT', 'NEXT_BAR']).map((m) => (
                                    <MenuItem key={m} value={m} sx={{ fontFamily, fontSize: '0.8rem' }}>{FILL_MODEL_LABELS[m] || m}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField size="small" fullWidth label="From bar" type="number" value={runSettings.start_bar} disabled={running}
                            placeholder={String(scenario?.data?.warmup ?? 0)}
                            onChange={(e) => setRunSettings((r) => ({ ...r, start_bar: e.target.value }))} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField size="small" fullWidth label="To bar" type="number" value={runSettings.end_bar} disabled={running}
                            placeholder={String(lastBar)}
                            onChange={(e) => setRunSettings((r) => ({ ...r, end_bar: e.target.value }))} sx={fieldSx} />
                    </Grid>
                </Grid>
                {ENGINE_NOTES[engineKind] && (
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#b45309', mt: 1 }}>{ENGINE_NOTES[engineKind]}</Typography>
                )}
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mt: 0.5 }}>
                    One cycle at each bar close inside the agent&apos;s trading window, as if Auto Execute were on. Nothing is sent to the broker
                    and nothing is added to the agent&apos;s Decision Log or orders. Partial fills, fees and option legs are not simulated.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    {!running && (
                        <Button variant="contained" disabled={!canEdit || busy || dirty || !scenario}
                            startIcon={<MuiIcons.PlayArrow />} onClick={() => startRun('start')}
                            sx={{ ...smallButtonSx, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                            Run simulation
                        </Button>
                    )}
                    {!running && run?.status === 'PAUSED' && (
                        <Button variant="outlined" disabled={!canEdit || busy || dirty} startIcon={<MuiIcons.SkipNext />}
                            onClick={() => startRun('continue')} sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                            Continue run
                        </Button>
                    )}
                    {running && (
                        <Button variant="outlined" color="error" startIcon={<MuiIcons.Stop />} onClick={() => { stopRef.current = true; }}
                            sx={smallButtonSx}>
                            Stop
                        </Button>
                    )}
                    {dirty && <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#b45309' }}>Save your changes to run on them.</Typography>}
                </Box>
                {running && (
                    <Box sx={{ mt: 1.5 }}>
                        <LinearProgress variant={run?.summary ? 'determinate' : 'indeterminate'}
                            value={run?.summary ? (100 * run.summary.bars_run) / Math.max(run.summary.bars_total, 1) : undefined}
                            sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: ACCENT }, bgcolor: '#fef3c7' }} />
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mt: 0.5 }}>
                            {run?.summary ? `${run.summary.bars_run} of ${run.summary.bars_total} bars` : 'Starting…'}
                        </Typography>
                    </Box>
                )}
            </Paper>
        );
    };

    const renderSummary = () => {
        const s = run?.summary;
        if (!s) return null;
        const cards = [
            { title: 'Total P&L', value: fmtMoney(s.total_pnl), color: pnlColor(s.total_pnl), sub: s.return_pct === null ? null : `${s.return_pct > 0 ? '+' : ''}${s.return_pct}% of ${fmtMoney(s.capital)}` },
            { title: 'Realized / Unrealized', value: `${fmtMoney(s.realized_pnl)} / ${fmtMoney(s.unrealized_pnl)}`, color: '#1e293b' },
            { title: 'Max drawdown', value: fmtMoney(s.max_drawdown), color: s.max_drawdown > 0 ? '#dc2626' : '#1e293b', sub: s.max_drawdown_pct === null ? null : `${s.max_drawdown_pct}%` },
            { title: 'Win rate', value: s.win_rate, color: '#1e293b', sub: s.wins_losses },
            { title: 'Orders', value: String(Object.values(s.orders || {}).reduce((a, b) => a + b, 0)), color: '#1e293b', sub: Object.entries(s.orders || {}).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(' · ') },
            { title: 'Final position', value: s.final_position ? (s.final_position.side === 'NONE' ? 'Flat' : `${s.final_position.side} ${fmtQty(s.final_position.qty)}`) : '—', color: '#1e293b', sub: s.final_position && s.final_position.side !== 'NONE' ? `avg ${fmtPrice(s.final_position.avg)} · depth ${s.final_position.depth}` : null },
        ];
        if (s.llm?.llm_calls) {
            cards.push({ title: 'Gemini', value: `${s.llm.llm_calls} calls`, color: '#1e293b', sub: `${(s.llm.input_tokens + s.llm.output_tokens).toLocaleString()} tokens · $${Number(s.llm.cost || 0).toFixed(4)}` });
        }
        return (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {cards.map((card) => (
                    <Grid item xs={6} md={4} lg key={card.title}>
                        <Paper sx={{ ...paperSx, p: 1.5, height: '100%' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>{card.title}</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: card.color, mt: 0.3 }}>{card.value}</Typography>
                            {card.sub && <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#94a3b8', mt: 0.2 }}>{card.sub}</Typography>}
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        );
    };

    // The run by trading day, when it spans more than one: a row moves the replay to that day.
    const renderByDate = () => {
        const days = run?.summary?.days || [];
        if (days.length < 2) return null;
        const gen = scenario?.generator;
        const patternOf = (date) => {
            const key = gen?.days?.[date]?.pattern || gen?.default?.pattern;
            return (catalog?.patterns || []).find((p) => p.key === key)?.label || key || '—';
        };
        const goToDay = (date) => {
            const index = steps.findIndex((s) => sessionOf(s) === date);
            if (index < 0) return;
            setPlaying(false);
            setCursor(index);
        };
        const headers = ['Date', ...(multi ? ['Pattern'] : []), 'Bars', 'Cycles', 'Fills', 'Day P&L', 'Realized', 'Cumulative P&L', 'Position at the close'];
        return (
            <Paper sx={{ ...paperSx, p: 0, mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', px: 2, pt: 2 }}>By date</Typography>
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', px: 2, pb: 1 }}>
                    One run across the dates: positions and resting orders carry overnight, DAY orders expire at each close. The days&apos; P&amp;L add up to the total.
                </Typography>
                <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {headers.map((h) => <TableCell key={h} sx={headCellSx}>{h}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {days.map((day) => (
                                <TableRow key={day.date} hover selected={day.date === currentSession} onClick={() => goToDay(day.date)}
                                    sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: `${ACCENT}14` } }}>
                                    <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                        {day.date}
                                        <Typography component="span" sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', ml: 0.75 }}>{weekdayOf(day.date)}</Typography>
                                    </TableCell>
                                    {multi && <TableCell sx={bodyCellSx}>{patternOf(day.date)}</TableCell>}
                                    <TableCell sx={bodyCellSx}>{day.bars}</TableCell>
                                    <TableCell sx={bodyCellSx}>{day.cycles}</TableCell>
                                    <TableCell sx={bodyCellSx}>{day.filled}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: pnlColor(day.pnl), whiteSpace: 'nowrap' }}>{fmtMoney(day.pnl)}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: pnlColor(day.realized), whiteSpace: 'nowrap' }}>{fmtMoney(day.realized)}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: pnlColor(day.cum_pnl), whiteSpace: 'nowrap' }}>{fmtMoney(day.cum_pnl)}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                        {day.position?.side && day.position.side !== 'NONE'
                                            ? `${day.position.side} ${fmtQty(day.position.qty)} @ ${fmtPrice(day.position.avg)}` : 'Flat'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    const renderReplay = () => {
        const step = currentStep;
        return (
            <Paper sx={{ ...paperSx, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>Replay</Typography>
                    <Tooltip title="Back one bar"><span>
                        <IconButton size="small" disabled={!steps.length || cursor === 0} onClick={() => { setPlaying(false); setCursor((c) => Math.max(c - 1, 0)); }}>
                            <MuiIcons.SkipPrevious fontSize="small" />
                        </IconButton>
                    </span></Tooltip>
                    <Tooltip title={playing ? 'Pause' : 'Play'}><span>
                        <IconButton size="small" disabled={!steps.length || running}
                            onClick={() => { if (!playing && cursor >= steps.length - 1) setCursor(0); setPlaying((p) => !p); }}
                            sx={{ color: ACCENT }}>
                            {playing ? <MuiIcons.Pause fontSize="small" /> : <MuiIcons.PlayArrow fontSize="small" />}
                        </IconButton>
                    </span></Tooltip>
                    <Tooltip title="Forward one bar"><span>
                        <IconButton size="small" disabled={!steps.length || cursor >= steps.length - 1} onClick={() => { setPlaying(false); setCursor((c) => Math.min(c + 1, steps.length - 1)); }}>
                            <MuiIcons.SkipNext fontSize="small" />
                        </IconButton>
                    </span></Tooltip>
                    <FormControl size="small" sx={{ ...fieldSx, width: 110 }}>
                        <Select value={speed} onChange={(e) => setSpeed(e.target.value)} sx={{ fontFamily, fontSize: '0.75rem' }}>
                            {PLAY_SPEEDS.map((s) => <MenuItem key={s} value={s} sx={{ fontFamily, fontSize: '0.75rem' }}>{s} bar{s > 1 ? 's' : ''}/s</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box sx={{ flex: 1, minWidth: 160, px: 1 }}>
                        <Slider size="small" min={0} max={Math.max(steps.length - 1, 0)} value={Math.min(cursor, Math.max(steps.length - 1, 0))}
                            disabled={!steps.length} onChange={(_, v) => { setPlaying(false); setCursor(v); }}
                            sx={{ color: ACCENT }} aria-label="Replay position" />
                    </Box>
                    {step && (
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#475569' }}>
                            Bar {step.i} · {step.time} · close {fmtPrice(step.close)}
                        </Typography>
                    )}
                </Box>
                <SimChart bars={savedBars} orders={run?.orders} steps={steps} upto={step ? step.i : null} height={320} showPnl focus={replayFocus} />
                {step && (
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.78rem' }}>
                            Position: <strong>{step.position.side === 'NONE' ? 'Flat' : `${step.position.side} ${fmtQty(step.position.qty)} @ ${fmtPrice(step.position.avg)}`}</strong>
                            {step.position.side !== 'NONE' && ` · depth ${step.position.depth}`}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.78rem' }}>
                            P&amp;L: <strong style={{ color: pnlColor(step.pnl.total) }}>{fmtMoney(step.pnl.total)}</strong>
                        </Typography>
                        {step.decisions.map((d, n) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Chip key={n} size="small" label={`${d.action}${d.phase ? ` · ${d.phase}` : ''}`}
                                sx={{ fontFamily, fontSize: '0.7rem', height: 22, fontWeight: 600, color: '#fff', bgcolor: ACTION_COLORS[d.action] || '#64748b' }} />
                        ))}
                        {step.ignored > 0 && (
                            <Tooltip title={`The answer listed ${step.ignored} more; a bar acts on the first ${step.decisions.length}.`}>
                                <Chip size="small" variant="outlined" label={`+${step.ignored} not run`} sx={{ fontFamily, fontSize: '0.7rem', height: 22, color: '#b45309', borderColor: '#f59e0b' }} />
                            </Tooltip>
                        )}
                        {step.status !== 'DECIDED' && STEP_STATUS[step.status] && <StatusChip spec={STEP_STATUS[step.status]} />}
                        {step.status !== 'CLOSED' && (
                            <Button size="small" startIcon={<MuiIcons.Insights sx={{ fontSize: '1rem' }} />} onClick={() => openInspect(step)}
                                sx={{ ...smallButtonSx, color: '#0ea5e9' }}>
                                Inspect this bar
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>
        );
    };

    const renderStepsTable = () => {
        const pageSteps = steps.slice(stepPage * STEPS_PER_PAGE, (stepPage + 1) * STEPS_PER_PAGE);
        return (
            <Paper sx={{ ...paperSx, p: 0, mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', px: 2, pt: 2, pb: 1 }}>Bar by bar</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Bar', 'Time', 'Close', 'Decision', 'Qty @ Price', 'Outcome', 'Fills', 'Position', 'P&L', ''].map((h) => (
                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageSteps.map((step, offset) => {
                                const index = stepPage * STEPS_PER_PAGE + offset;
                                const decisions = step.decisions.length ? step.decisions : [null];
                                const selected = index === Math.min(cursor, steps.length - 1);
                                return decisions.map((d, n) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableRow key={`${step.i}-${n}`} hover selected={selected} onClick={() => { setPlaying(false); setCursor(index); }}
                                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: `${ACCENT}14` } }}>
                                        <TableCell sx={bodyCellSx}>{n === 0 ? step.i : ''}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{n === 0 ? step.time : ''}</TableCell>
                                        <TableCell sx={bodyCellSx}>{n === 0 ? fmtPrice(step.close) : ''}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, maxWidth: 320 }}>
                                            {d ? (
                                                <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{[d.note, d.reasoning].filter(Boolean).join('\n\n') || 'No reasoning'}</span>}>
                                                    <Box>
                                                        <Typography component="span" sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: ACTION_COLORS[d.action] || '#64748b' }}>{d.action}</Typography>
                                                        {d.phase && <Typography component="span" sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', ml: 0.75 }}>{d.phase}</Typography>}
                                                    </Box>
                                                </Tooltip>
                                            ) : STEP_STATUS[step.status] ? <StatusChip spec={STEP_STATUS[step.status]} label={step.status === 'FAILED' && step.error ? `Failed: ${step.error.slice(0, 60)}` : (step.kill ? `Kill switch: ${step.kill}` : undefined)} /> : '—'}
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                            {d && d.action !== 'HOLD' ? `${d.qty === null ? 'all' : fmtQty(d.qty)}${d.price ? ` @ ${fmtPrice(d.price)}` : ' mkt'}` : ''}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d && OUTCOME[d.outcome] && d.outcome !== 'HOLD' && (
                                                <Tooltip title={d.note || ''}><span><StatusChip spec={OUTCOME[d.outcome]} /></span></Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                            {n === 0 && step.fills.map((f) => `#${f.order} ${f.side} ${fmtQty(f.qty)} @ ${fmtPrice(f.price)}`).join(', ')}
                                            {n === 0 && step.ended.map((e) => ` #${e.order} ${e.status.toLowerCase()}`).join(',')}
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                            {n === 0 && (step.position.side === 'NONE' ? 'Flat' : `${step.position.side} ${fmtQty(step.position.qty)}`)}
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: pnlColor(step.pnl.total), fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {n === 0 ? fmtMoney(step.pnl.total) : ''}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {n === 0 && step.status !== 'CLOSED' && (
                                                <Tooltip title="Inspect this bar">
                                                    <IconButton size="small" aria-label={`Inspect bar ${step.i}`}
                                                        onClick={(e) => { e.stopPropagation(); setCursor(index); openInspect(step); }}
                                                        sx={{ p: 0.3, color: '#0ea5e9' }}>
                                                        <MuiIcons.Insights sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ));
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination component="div" count={steps.length} page={Math.min(stepPage, Math.max(Math.ceil(steps.length / STEPS_PER_PAGE) - 1, 0))}
                    rowsPerPage={STEPS_PER_PAGE} rowsPerPageOptions={[STEPS_PER_PAGE]} onPageChange={(_, p) => setStepPage(p)}
                    sx={{ fontFamily, '& .MuiTablePagination-displayedRows': { fontFamily } }} />
            </Paper>
        );
    };

    const renderOrders = () => {
        const orders = (run?.orders || []).filter((o) => !o.start);
        if (!orders.length) return null;
        return (
            <Paper sx={{ ...paperSx, p: 0 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', px: 2, pt: 2, pb: 1 }}>Simulated orders</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['#', 'Sent at bar', 'Action', 'Side', 'Type', 'Qty', 'Limit', 'Status', 'Filled at bar', 'Fill price', 'Phase'].map((h) => (
                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((o) => (
                                <TableRow key={o.id}>
                                    <TableCell sx={bodyCellSx}>{o.id}</TableCell>
                                    <TableCell sx={bodyCellSx}>{o.bar}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACTION_COLORS[o.action] || '#1e293b' }}>{o.action}</TableCell>
                                    <TableCell sx={bodyCellSx}>{o.side}</TableCell>
                                    <TableCell sx={bodyCellSx}>{o.type} {o.tif}</TableCell>
                                    <TableCell sx={bodyCellSx}>{fmtQty(o.qty)}</TableCell>
                                    <TableCell sx={bodyCellSx}>{o.limit === null ? '—' : fmtPrice(o.limit)}</TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Tooltip title={o.note || ''}><span><StatusChip spec={ORDER_STATUS[o.status] || ORDER_STATUS.CANCELLED} label={o.status} /></span></Tooltip>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>{o.fill_bar ?? '—'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{o.fill_price === null ? '—' : fmtPrice(o.fill_price)}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: '#64748b' }}>{o.phase || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    const renderRun = () => (
        <>
            {renderRunSettings()}
            {dirty && run && (
                <Alert severity="warning" sx={{ fontFamily, fontSize: '0.8rem', mb: 2 }}>
                    The results below are from the saved scenario. Your unsaved edits are not in them.
                </Alert>
            )}
            {run?.status === 'FAILED' && run.error && (
                <Alert severity="error" sx={{ fontFamily, fontSize: '0.8rem', mb: 2 }}>{run.error}</Alert>
            )}
            {!run && !running && (
                <Paper sx={{ ...paperSx, textAlign: 'center', py: 5 }}>
                    <MuiIcons.Science sx={{ fontSize: 40, color: '#cbd5e1' }} />
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 1 }}>
                        Run the simulation to see what {agent.name} decides on every bar of this scenario.
                    </Typography>
                </Paper>
            )}
            {run && (
                <>
                    {run.engine?.label && (
                        <Typography component="div" sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', mb: 1 }}>
                            Ran {run.engine.label} · {FILL_MODEL_LABELS[run.options?.fill_model]?.split(':')[0] || run.options?.fill_model} fills ·
                            bars {run.options?.start_bar} to {run.options?.end_bar}
                            {run.status && RUN_STATUS[run.status] && <Box component="span" sx={{ ml: 1 }}><StatusChip spec={RUN_STATUS[run.status]} /></Box>}
                        </Typography>
                    )}
                    {renderSummary()}
                    {renderByDate()}
                    {renderReplay()}
                    {renderStepsTable()}
                    {renderOrders()}
                </>
            )}
        </>
    );

    // ==================== RENDER: INSPECT DRAWER ====================

    const renderInspect = () => {
        const data = inspect?.data;
        const step = data?.step;
        const engine = data?.engine;
        return (
            <Drawer anchor="right" open={Boolean(inspect)} onClose={() => setInspect(null)}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
                PaperProps={{ sx: { width: { xs: '100%', md: 'calc(100vw - 240px)' }, bgcolor: '#f8fafc' } }}>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MuiIcons.Insights sx={{ color: '#0ea5e9' }} />
                    <Typography sx={{ fontFamily, fontWeight: 600 }}>
                        Bar {inspect?.bar}{step ? ` · ${step.time}` : ''}: what {agent.name} saw and decided
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <IconButton aria-label="Close" onClick={() => setInspect(null)}><MuiIcons.Close /></IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                    {inspect?.loading && <Skeleton variant="rectangular" height={240} sx={{ borderRadius: '12px' }} />}
                    {inspect?.error && <Alert severity="error" sx={{ fontFamily }}>{inspect.error}</Alert>}
                    {data && (
                        <>
                            <Paper sx={{ ...paperSx, mb: 2 }}>
                                <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Decision</Typography>
                                {step?.status === 'KILL_SWITCH' && <Alert severity="error" sx={{ fontFamily, fontSize: '0.8rem', mb: 1 }}>Kill switch: {step.kill}. Live ends such a cycle with no decision.</Alert>}
                                {step?.status === 'FAILED' && <Alert severity="error" sx={{ fontFamily, fontSize: '0.8rem', mb: 1 }}>{step.error}</Alert>}
                                {step?.ignored > 0 && <Alert severity="warning" sx={{ fontFamily, fontSize: '0.8rem', mb: 1 }}>The answer listed {step.ignored} more {step.ignored === 1 ? 'decision' : 'decisions'}. A bar acts on the first {step.decisions.length}; the rest were not run.</Alert>}
                                {(step?.decisions || []).map((d, n) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <Box key={n} sx={{ mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip size="small" label={d.action} sx={{ fontFamily, fontWeight: 700, color: '#fff', bgcolor: ACTION_COLORS[d.action] || '#64748b' }} />
                                            {d.phase && <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{d.phase}</Typography>}
                                            {d.action !== 'HOLD' && <Typography sx={{ fontFamily, fontSize: '0.8rem' }}>{d.qty === null ? 'whole position' : fmtQty(d.qty)}{d.price ? ` @ ${fmtPrice(d.price)}` : ' at the market'}</Typography>}
                                            {OUTCOME[d.outcome] && d.outcome !== 'HOLD' && <StatusChip spec={OUTCOME[d.outcome]} />}
                                        </Box>
                                        {d.note && <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#b45309', mt: 0.5 }}>{d.note}</Typography>}
                                        {d.reasoning && <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#475569', mt: 0.5, whiteSpace: 'pre-wrap' }}>{d.reasoning}</Typography>}
                                    </Box>
                                ))}
                            </Paper>
                            {engine?.error && <Alert severity="error" sx={{ fontFamily, fontSize: '0.8rem', mb: 2 }}>{engine.error}</Alert>}
                            {engine?.kind === 'RULE' && engine.trace && (
                                <Paper sx={{ ...paperSx, mb: 2 }}>
                                    <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Rule steps on this bar</Typography>
                                    <TraceView trace={engine.trace} />
                                </Paper>
                            )}
                            {engine?.kind === 'ROUTER' && engine.router && (
                                <Paper sx={{ ...paperSx, mb: 2 }}>
                                    <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Router</Typography>
                                    {(engine.router.decisions || []).map((d, n) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Typography key={n} sx={{ fontFamily, fontSize: '0.78rem' }}><strong>{d.phase}</strong>: {d.reasoning}</Typography>
                                    ))}
                                </Paper>
                            )}
                            {engine?.kind === 'LLM' && (
                                <Paper sx={{ ...paperSx, mb: 2 }}>
                                    <Typography sx={{ ...sectionTitleSx, mb: 1 }}>
                                        Gemini&apos;s answer{engine.llm?.input_tokens ? ` (${engine.llm.input_tokens} in / ${engine.llm.output_tokens} out tokens)` : ''}
                                    </Typography>
                                    <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, maxHeight: 360, overflow: 'auto', bgcolor: '#f8fafc', p: 1.5, borderRadius: '8px' }}>
                                        {engine.llm?.raw || 'No answer was stored for this bar.'}
                                    </Box>
                                </Paper>
                            )}
                            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Market data at this bar&apos;s close</Typography>
                            {renderSnapshot ? renderSnapshot(data) : null}
                        </>
                    )}
                </Box>
            </Drawer>
        );
    };

    // ==================== RENDER: DIALOGS ====================

    const renderGeneratorDialog = () => {
        if (!generator) return null;
        const g = generator;
        const set = (change) => setGenerator((cur) => ({ ...cur, ...change }));
        const err = (key) => g.errors?.[key];
        const pattern = (catalog?.patterns || []).find((p) => p.key === g.pattern);
        const defaultVol = catalog?.defaults?.volatility_pct?.[g.timeframe];
        return (
            <Dialog open onClose={() => !busy && setGenerator(null)} maxWidth={g.multi ? 'lg' : 'md'} fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>
                    {g.mode === 'create' ? (g.multi ? 'New multi-date scenario' : 'New scenario') : 'Regenerate scenario'}
                </DialogTitle>
                <DialogContent dividers>
                    {g.multi ? (
                        <>
                            {g.mode === 'create' && (
                                <TextField size="small" fullWidth label="Name" value={g.name} onChange={(e) => set({ name: e.target.value })}
                                    sx={{ ...fieldSx, mb: 2 }} inputProps={{ maxLength: 120 }} />
                            )}
                            {/* An edit clears the last submit's errors: the preview checks the form again as it changes. */}
                            <MultiDateGeneratorFields form={g} set={(change) => set({ ...change, errors: {} })} catalog={catalog}
                                agent={agent} api={api} optionsLabels={OPTIONS_LEVEL_LABELS} />
                        </>
                    ) : (
                        <Grid container spacing={2}>
                            {g.mode === 'create' && (
                                <Grid item xs={12}>
                                    <TextField size="small" fullWidth label="Name" value={g.name} onChange={(e) => set({ name: e.target.value })} sx={fieldSx} inputProps={{ maxLength: 120 }} />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <FormControl size="small" fullWidth sx={fieldSx} error={Boolean(err('pattern'))}>
                                    <InputLabel>Pattern</InputLabel>
                                    <Select label="Pattern" value={g.pattern} onChange={(e) => set({ pattern: e.target.value })}>
                                        {(catalog?.patterns || []).map((p) => <MenuItem key={p.key} value={p.key} sx={{ fontFamily, fontSize: '0.8rem' }}>{p.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                {pattern && <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mt: 0.5 }}>{pattern.description}</Typography>}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl size="small" fullWidth sx={fieldSx}>
                                    <InputLabel>Timeframe</InputLabel>
                                    <Select label="Timeframe" value={g.timeframe} onChange={(e) => set({ timeframe: e.target.value })}>
                                        {(catalog?.timeframes || []).map((tf) => <MenuItem key={tf} value={tf} sx={{ fontFamily, fontSize: '0.8rem' }}>{TimeFrameLabels[tf] || tf}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mt: 0.5 }}>
                                    Bars fall inside the agent&apos;s trading window ({agent.trading_start_time && agent.trading_end_time ? `${agent.trading_start_time}–${agent.trading_end_time}` : '24/7'} {agent.trading_timezone}).
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Bars" type="number" value={g.bars} onChange={(e) => set({ bars: e.target.value })}
                                    error={Boolean(err('bars'))} helperText={err('bars') || `${catalog?.limits?.min_bars || 5} to ${catalog?.limits?.max_bars || 500}`} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Warm-up bars" type="number" value={g.warmup} onChange={(e) => set({ warmup: e.target.value })}
                                    error={Boolean(err('warmup'))} helperText={err('warmup') || 'History before the first decision'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Start date" type="date" value={g.start_date} onChange={(e) => set({ start_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }} error={Boolean(err('start_date'))} helperText={err('start_date') || 'Blank: today'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Start price" type="number" value={g.start_price} onChange={(e) => set({ start_price: e.target.value })}
                                    error={Boolean(err('start_price'))} helperText={err('start_price') || 'Blank: the live price'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Volatility % per bar" type="number" value={g.volatility_pct} onChange={(e) => set({ volatility_pct: e.target.value })}
                                    error={Boolean(err('volatility_pct'))} helperText={err('volatility_pct') || `Blank: ${defaultVol ?? '—'}%`} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Drift % per bar" type="number" value={g.trend_pct} onChange={(e) => set({ trend_pct: e.target.value })}
                                    disabled={g.pattern !== 'RANDOM_WALK'} error={Boolean(err('trend_pct'))} helperText={err('trend_pct') || 'Random walk only'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Volume per bar" type="number" value={g.volume} onChange={(e) => set({ volume: e.target.value })}
                                    error={Boolean(err('volume'))} helperText={err('volume') || 'Blank: a typical volume'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField size="small" fullWidth label="Seed" type="number" value={g.seed} onChange={(e) => set({ seed: e.target.value })}
                                    error={Boolean(err('seed'))} helperText={err('seed') || 'Same seed, same bars. Blank: random'} sx={fieldSx} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl size="small" fullWidth sx={fieldSx}>
                                    <InputLabel>Options levels</InputLabel>
                                    <Select label="Options levels" value={g.options_levels} onChange={(e) => set({ options_levels: e.target.value })}>
                                        {(catalog?.options_levels || ['AUTO', 'LIVE', 'NONE']).map((o) => <MenuItem key={o} value={o} sx={{ fontFamily, fontSize: '0.8rem' }}>{OPTIONS_LEVEL_LABELS[o] || o}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={<Switch checked={Boolean(g.use_live_context)} onChange={(e) => set({ use_live_context: e.target.checked })} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Start from the agent&apos;s live market context (macro, SPY, crypto, flags)</Typography>}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {g.mode === 'regenerate' && (
                        <Alert severity="warning" sx={{ fontFamily, fontSize: '0.78rem', mt: 2 }}>
                            Regenerating replaces the bars, the market context and every edit, and clears the run.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setGenerator(null)} disabled={busy} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitGenerator} disabled={busy}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <MuiIcons.AutoFixHigh />}
                        sx={{ ...smallButtonSx, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {g.mode === 'create' ? 'Generate' : 'Regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderDetailsDialog = () => details && (
        <Dialog open onClose={() => !busy && setDetails(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>Scenario name and notes</DialogTitle>
            <DialogContent dividers>
                <TextField size="small" fullWidth label="Name" value={details.name} inputProps={{ maxLength: 120 }}
                    onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} sx={{ ...fieldSx, mb: 2 }} />
                <TextField size="small" fullWidth multiline minRows={3} label="Notes" value={details.notes || ''} inputProps={{ maxLength: 2000 }}
                    onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))} sx={fieldSx} />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setDetails(null)} disabled={busy} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={saveDetails} disabled={busy} sx={{ ...smallButtonSx, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>Save</Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER ====================

    const renderEmpty = () => (
        <Paper sx={{ ...paperSx, flex: 1, textAlign: 'center', py: 6 }}>
            {multi ? <MuiIcons.DateRange sx={{ fontSize: 48, color: '#cbd5e1' }} /> : <MuiIcons.Science sx={{ fontSize: 48, color: '#cbd5e1' }} />}
            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', mt: 1 }}>{multi ? 'Multi-date scenarios' : 'Data Simulator'}</Typography>
            {multi ? (
                <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mt: 1, maxWidth: 560, mx: 'auto' }}>
                    Generate fake market data for {agent.name} ({agent.symbol}) over a range of dates, a pattern for each date, then run
                    the agent across them in one go: positions carry overnight, and the results come by date. It decides with its
                    own {agent.execution_mode === 'LLM' ? 'prompt' : 'rule or router'}, with simulated fills: nothing reaches the broker.
                </Typography>
            ) : (
                <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mt: 1, maxWidth: 560, mx: 'auto' }}>
                    Generate fake market data for {agent.name} ({agent.symbol}), edit any bar or market value, then run the agent over it.
                    It decides with its own {agent.execution_mode === 'LLM' ? 'prompt' : 'rule or router'}, bar by bar, with simulated fills:
                    nothing reaches the broker.
                </Typography>
            )}
            {canAdd && (
                <Button variant="contained" startIcon={<MuiIcons.AutoFixHigh />} disabled={!catalog || busy}
                    onClick={() => openGenerator('create')} sx={{ ...smallButtonSx, mt: 2, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                    {multi ? 'Generate a multi-date scenario' : 'Generate a scenario'}
                </Button>
            )}
        </Paper>
    );

    // A multi-date scenario's dates and how they were generated, for its header.
    const multiDateSubtitle = (gen) => {
        const label = (catalog?.patterns || []).find((p) => p.key === gen.default?.pattern)?.label || gen.default?.pattern;
        const own = Object.keys(gen.days || {}).length;
        const { dates } = gen;
        return `${dates.length} date${dates.length === 1 ? '' : 's'}, ${dates[0]} to ${dates[dates.length - 1]} · generated: ${label}`
            + `${own ? `, ${own} ${own === 1 ? 'date with its' : 'dates with their'} own settings` : ''}, seed ${gen.seed}`;
    };

    const renderScenario = () => {
        if (scenarioLoading || !scenario || !draft) {
            return <Box sx={{ flex: 1 }}><Skeleton variant="rectangular" height={420} sx={{ borderRadius: '12px' }} /></Box>;
        }
        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Paper sx={{ ...paperSx, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>{scenario.name}</Typography>
                            {canEdit && (
                                <IconButton size="small" aria-label="Edit name and notes" disabled={busy || running}
                                    onClick={() => setDetails({ name: scenario.name, notes: scenario.notes || '' })} sx={{ p: 0.3, color: '#94a3b8' }}>
                                    <MuiIcons.Edit sx={{ fontSize: '0.95rem' }} />
                                </IconButton>
                            )}
                            {dirty && <Chip label="unsaved" size="small" sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#fef3c7', color: '#b45309' }} />}
                        </Box>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                            {draft.bars.length} bars · {TimeFrameLabels[draft.timeframe] || draft.timeframe}
                            {scenario.generator?.pattern && ` · generated: ${(catalog?.patterns || []).find((p) => p.key === scenario.generator.pattern)?.label || scenario.generator.pattern}, seed ${scenario.generator.seed}`}
                            {multi && scenario.generator?.dates?.length > 0 && ` · ${multiDateSubtitle(scenario.generator)}`}
                            {scenario.notes && ` · ${scenario.notes}`}
                        </Typography>
                    </Box>
                    {dirty && (
                        <>
                            <Button size="small" variant="outlined" disabled={busy} startIcon={<MuiIcons.Undo />} onClick={discardDraft}
                                sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                                Discard
                            </Button>
                            <Button size="small" variant="contained" disabled={busy || !canEdit} onClick={saveData}
                                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <MuiIcons.Save />}
                                sx={{ ...smallButtonSx, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
                                Save
                            </Button>
                        </>
                    )}
                    {canEdit && (
                        <Button size="small" variant="outlined" disabled={busy || running} startIcon={<MuiIcons.RestartAlt />}
                            onClick={() => { if (!dirty || window.confirm(DISCARD_CHANGES)) openGenerator('regenerate'); }}
                            sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                            Regenerate
                        </Button>
                    )}
                </Paper>
                <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                    <Tabs value={view} onChange={(_, v) => setView(v)}
                        sx={{
                            minHeight: 42,
                            '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 500, minHeight: 42, fontSize: '0.82rem' },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT },
                        }}>
                        <Tab value="data" label="Bars" />
                        <Tab value="context" label="Market Context" />
                        <Tab value="run" label={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                Run &amp; Results
                                {running && <CircularProgress size={12} sx={{ color: ACCENT }} />}
                            </Box>
                        )} />
                    </Tabs>
                </Paper>
                {view === 'data' && renderData()}
                {view === 'context' && renderContext()}
                {view === 'run' && renderRun()}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {renderList()}
            {selectedId ? renderScenario() : renderEmpty()}
            {renderGeneratorDialog()}
            {renderDetailsDialog()}
            {renderInspect()}
        </Box>
    );
};

export default AIAgentDataSimulator;
